/**
 * "Insights" panel (NAU-247 / NAU-266).
 *
 * Four insight tiles for the selected period, derived from the same
 * `/dashboard/status` read as the summary cards (see `buildInsights`): the
 * completion-rate improvement, the most active staff member, late checks vs the
 * prior period, and the worst current status across the nursery.
 */

import { Badge } from "@package/subframe/components/Badge";
import type { LucideIcon } from "lucide-react";
import { AlertTriangle, Gauge, ShieldCheck, UserRound } from "lucide-react";

import type { DashboardInsights } from "@/lib/dashboard/insights";
import {
  type DashboardPeriod,
  previousPeriodLabels,
} from "@/lib/dashboard/period";
import {
  DASHBOARD_STATUS_META,
  type DashboardStatus,
} from "@/lib/dashboard/traffic-light";

import { OverviewCard, SectionHeader } from "./_shared";
import { NaudBadgeIcon, type NaudTone } from "./NaudIcon";

function InsightColumn({
  icon,
  tone,
  title,
  value,
  foot,
  first,
}: {
  icon: LucideIcon;
  tone: NaudTone;
  title: string;
  value: React.ReactNode;
  foot: string;
  first?: boolean;
}) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-3 ${
        first
          ? ""
          : "@3xl:border-l @3xl:border-solid @3xl:border-neutral-100 @3xl:pl-5"
      }`}
    >
      <NaudBadgeIcon size="md" tone={tone} icon={icon} />
      <span className="text-body-bold font-body-bold leading-snug text-primary-navy-800 wrap-break-word">
        {title}
      </span>
      <div>{value}</div>
      <span className="text-caption font-caption text-subtext-color">
        {foot}
      </span>
    </div>
  );
}

const PERIOD_NOUN: Record<DashboardPeriod, string> = {
  today: "today",
  this_week: "this week",
  this_month: "this month",
};

/** Tone for the overall-status tile, following the traffic-light bands. */
const BAND_TONE: Record<DashboardStatus, NaudTone> = {
  ON_TRACK: "green",
  NEEDS_ATTENTION: "orange",
  ACTION: "red",
};

/** Completion-rate improvement: green up / red down / neutral for no change or no baseline. */
function CompletionImprovement({ delta }: { delta: number | null }) {
  if (delta === null) {
    return (
      <span className="text-heading-2 font-heading-2 text-neutral-400">—</span>
    );
  }
  const color =
    delta > 0
      ? "text-success-700"
      : delta < 0
        ? "text-error-700"
        : "text-neutral-900";
  const arrow = delta > 0 ? "↑ " : delta < 0 ? "↓ " : "";
  return (
    <span className={`text-heading-2 font-heading-2 ${color}`}>
      {arrow}
      {Math.abs(delta)}%
    </span>
  );
}

function lateChecksFoot(delta: number, period: DashboardPeriod): string {
  const previous = previousPeriodLabels[period];
  if (delta < 0) return `${Math.abs(delta)} fewer than ${previous}`;
  if (delta > 0) return `${delta} more than ${previous}`;
  return `Same as ${previous}`;
}

/** Muted placeholders shown while the status query loads, so the tiles don't
 *  flash a falsely-reassuring all-clear before data arrives. */
function SkeletonColumns() {
  return (
    <div className="mt-5 grid grid-cols-1 gap-5 @xs:grid-cols-2 @3xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex min-w-0 flex-col gap-3">
          <div className="h-12 w-12 animate-pulse rounded-full bg-neutral-100" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-neutral-100" />
          <div className="h-6 w-1/2 animate-pulse rounded bg-neutral-100" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-neutral-100" />
        </div>
      ))}
    </div>
  );
}

export function InsightsPanel({
  insights,
  period,
  loading,
}: {
  insights: DashboardInsights;
  period: DashboardPeriod;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <OverviewCard className="w-full p-5 @container">
        <SectionHeader title="Insights" />
        <SkeletonColumns />
      </OverviewCard>
    );
  }

  const { mostActive, overallBand } = insights;
  // Only credit a "most active" person when they actually completed something —
  // a zero count would otherwise name someone under "0 completed".
  const activeStaff = mostActive && mostActive.count > 0 ? mostActive : null;
  const bandMeta = DASHBOARD_STATUS_META[overallBand];

  return (
    <OverviewCard className="w-full p-5 @container">
      <SectionHeader title="Insights" />

      <div className="mt-5 grid grid-cols-1 gap-5 @xs:grid-cols-2 @3xl:grid-cols-4">
        <InsightColumn
          first
          icon={Gauge}
          tone="blue"
          title="Completion rate improvement"
          value={
            <CompletionImprovement delta={insights.completionImprovement} />
          }
          foot={`vs ${previousPeriodLabels[period]}`}
        />
        <InsightColumn
          icon={UserRound}
          tone="purple"
          title="Most active"
          value={
            <span className="text-heading-3 font-heading-3 text-primary-navy-800">
              {activeStaff ? activeStaff.name : "No activity yet"}
            </span>
          }
          foot={
            activeStaff
              ? `${activeStaff.count} completed ${PERIOD_NOUN[period]}`
              : `No completions ${PERIOD_NOUN[period]}`
          }
        />
        <InsightColumn
          icon={AlertTriangle}
          tone="orange"
          title="Late checks"
          value={
            <span className="text-heading-2 font-heading-2 text-neutral-900">
              {insights.lateChecks.count}
            </span>
          }
          foot={lateChecksFoot(insights.lateChecks.delta, period)}
        />
        <InsightColumn
          icon={ShieldCheck}
          tone={BAND_TONE[overallBand]}
          title="Overall status now"
          value={
            <Badge variant={bandMeta.badgeVariant}>{bandMeta.label}</Badge>
          }
          foot="Worst status across the nursery"
        />
      </div>
    </OverviewCard>
  );
}
